<template>
  <base-page-layout ref="base-page" :title="alteracaoTitle" :hide-breadcrumbs="$getFirstValue()" class="alteracao-vouchers">
    <v-container class="pa-0" fluid>
      <v-row>
        <v-col>
          <title-description
            :title.sync="voucher.title"
            :description.sync="voucher.description"
            :title-error="$v.voucher.title.$error"
            :title-error-message="errorTitle"
            :description-error="$v.voucher.description.$error"
            :description-error-message="errorDescription"
            :status="voucher.status"
            :disable="isActive"
            mode="PONTOS"
          ></title-description>
        </v-col>
      </v-row>
      <v-container class="pa-0" fluid>
        <v-row>
          <v-col cols="6">
            <v-card>
              <v-card-text class="pb-0">
                <h3 class="periodo-card-title">Desconto</h3>
                <p class="legenda">É necessário definir o valor deste cupom de desconto. Esse é o valor que será utilizado em suas lojas.</p>

                <v-text-field
                  v-model="money"
                  v-money="vPoints"
                  label="Valor do desconto"
                  placeholder="0"
                  outlined
                  :maxlength="4"
                  prefix="R$"
                  :error-messages="($v.voucher.money.$error && errorMoney) || ''"
                  :disabled="isActive"
                  data-cy="discountvalue"
                />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card>
              <v-card-text class="pb-0">
                <h3 class="periodo-card-title">Custo em pontos</h3>
                <p class="legenda">Defina quantos pontos o consumidor precisará ter para poder comprar este cupom de descontos.</p>

                <v-text-field
                  v-model="points"
                  v-money="vPoints"
                  label="Custo em pontos"
                  outlined
                  :maxlength="11"
                  :error-messages="($v.voucher.points.$error && errorPoints) || ''"
                  :disabled="isActive"
                  data-cy="points"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-card class="py-1 px-4">
        <measuring-period-configuration
          label-inicio="Data de início"
          label-final="Data de fim"
          error-messages="Selecione o período de validade"
          label="Período de validade"
          class="my-3 pb-0"
          :initial-date.sync="voucher.initial_date"
          :final-date.sync="voucher.final_date"
          :initial-date-error="getSafe(() => $v.voucher.initial_date.$error)"
          :final-date-error="getSafe(() => $v.voucher.final_date.$error)"
          :initial-disabled="isActive"
          :cols="3"
          :edit-only-end-date="voucher.status === 'ACTIVE'"
        >
          <template v-slot:titulo>Data de início e fim</template>
          Aqui você define o período em que esse voucher estará ativo para ser comprado pelo consumidor. Após esse período, os vouchers previamente comprados
          permanecem ativos e ainda poderão ser utilizados.
        </measuring-period-configuration>
      </v-card>
      <action-buttons :save="save" :object="voucher" mode="pontos" />
    </v-container>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import ActionButtons from '../components/ActionButtons.vue'
import MeasuringPeriodConfiguration from '@/modules/campanhas/views/components/edit/MeasuringPeriodConfiguration.vue'

import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { deepDelete } from '@/helpers/deepDelete'
import { VMoney } from 'v-money'

import { MUTATION_UPSERT_DRAFT_VOUCHER, MUTATION_UPSERT_VOUCHER } from '../../graphql'
import Swal from 'sweetalert2'
import { cloneDeep } from 'lodash'
import { DateTime } from 'luxon'

export default {
  components: {
    BasePageLayout,
    ActionButtons,
    MeasuringPeriodConfiguration,
    TitleDescription: () => import('@/modules/campanhas/views/components/edit/TitleDescription.vue'),
  },
  directives: { money: VMoney },
  props: {
    voucher: Object,
  },
  data: () => ({
    vPoints: { thousands: '.', precision: 0 },
    savingDraft: false,
    moneyLocal: '',
    pointsLocal: '',
  }),
  computed: {
    money: {
      get() {
        return this.moneyLocal
      },
      set(value) {
        let v = value.split('.').join('').split(',').join('.')
        this.voucher.money = Number(v.replace(/[^0-9.]/g, '')) > 0 ? Number(v.replace(/[^0-9.]/g, '')) : ''
        this.moneyLocal = value
      },
    },
    points: {
      get() {
        return this.pointsLocal
      },
      set(value) {
        let p = value.split('.').join('')
        this.voucher.points = Number(p.replace(/[^0-9.]/g, '')) > 0 ? Number(p.replace(/[^0-9.]/g, '')) : ''
        this.pointsLocal = value
      },
    },
    isActive() {
      return Boolean(this.voucher.status === 'ACTIVE' || this.voucher.status === 'FINISHED')
    },
    isFinished() {
      return Boolean(this.voucher.status === 'FINISHED')
    },
    minDate() {
      if (DateTime.now().toFormat('HH') < DateTime.fromObject({ hour: 20 }).toFormat('HH')) {
        return DateTime.now().toFormat('yyyy-MM-dd')
      }
      return DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd')
    },
    errorSendDate() {
      if (!this.$v.voucher || !this.$v.voucher.initial_date) return ''
      if (this.$v.voucher.initial_date.$error) {
        if (!this.$v.voucher.initial_date.required) return 'Selecione uma data de envio.'
        else if (!this.$v.voucher.initial_date.isFuture) return 'A data de envio deve ser no futuro.'
      }
      return ''
    },
    errorMoney() {
      if (!this.$v.voucher || !this.$v.voucher.money) return ''
      if (this.$v.voucher.money.$error) return 'Defina o valor do cupom de desconto.'
      return ''
    },
    errorPoints() {
      if (!this.$v.voucher || !this.$v.voucher.points) return ''
      if (this.$v.voucher.points.$error) return 'Defina quantos pontos são necessários.'
      return ''
    },
    errorFinalDate() {
      if (!this.$v.voucher || !this.$v.voucher.final_date) return ''
      if (this.$v.voucher.final_date.$error) {
        if (!this.$v.voucher.final_date.required) return 'Selecione uma data de envio.'
        else if (!this.$v.voucher.final_date.isFuture) return 'A data de envio deve ser no futuro.'
      }
      return ''
    },
    errorTitle() {
      if (!this.$v.voucher.title) return ''
      if (this.$v.voucher.title.$error) {
        if (!this.$v.voucher.title.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.voucher.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.voucher.title.maxLength) return 'O título excedeu 70 caracteres.'
        else if (!this.$v.voucher.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.voucher.description) return ''
      if (this.$v.voucher.description.$error) {
        if (!this.$v.voucher.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        else if (!this.$v.voucher.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.voucher.description.maxLength) return 'A descrição excedeu 150 caracteres.'
        else if (!this.$v.voucher.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    alteracaoTitle() {
      if (this.newVoucher) return 'Novo Voucher'
      else return 'Editar Voucher'
    },
    newVoucher() {
      let path = this.$router.currentRoute.fullPath
      return path.includes('novo') && true
    },
  },
  watch: {
    initialDate(value) {
      if (DateTime.fromISO(value) > DateTime.fromISO(this.voucher.final_date)) this.errorDateFuture = 'A data de envio deve ser no futuro.'
      else this.errorDateFuture = ''
    },
    finalDate(value) {
      if (DateTime.fromISO(this.voucher.initial_date) > DateTime.fromISO(value)) this.errorDateFuture = 'A data de envio deve ser no futuro.'
      else this.errorDateFuture = ''
    },
  },
  created() {
    if (!this.newVoucher) {
      this.pointsLocal = this.voucher.points
      this.moneyLocal = this.voucher.money
    }
  },
  mounted() {},
  methods: {
    async save(isDraft) {
      this.savingDraft = isDraft

      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return
      }

      const voucher = cloneDeep(this.voucher)
      deepDelete(voucher, ['__typename', 'status'])
      if (isDraft) {
        voucher.money = voucher.money === '' ? 0 : voucher.money
        voucher.points = voucher.points === '' ? 0 : voucher.points
      }

      const mutation = isDraft ? MUTATION_UPSERT_DRAFT_VOUCHER : MUTATION_UPSERT_VOUCHER

      await this.$apollo.mutate({
        mutation: mutation,
        variables: {
          data: voucher,
        },
      })

      Swal.fire({
        text: 'Seu voucher foi salvo',
        icon: 'success',
        showConfirmButton: false,
        timer: 1000,
        customClass: {
          content: 'content-class',
        },
      })

      this.$router.push('/mobile/pontos').catch(() => {})
    },
  },
  validations() {
    return {
      voucher: {
        title: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(70),
          notOnlySpace,
        },
        description: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(150),
          notOnlySpace,
        },
        initial_date: {
          required,
        },
        final_date: {
          required,
        },
        money: {
          required,
        },
        points: {
          required,
        },
      },
    }
  },
}
</script>

<style lang="scss">
$phone-size: 600px;
$tablet-size: 960px;
.alteracao-vouchers {
  min-width: 1020px;
  h3 {
    margin-bottom: 8px;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.33px;
    color: $cinza-d20;
  }
  h4 {
    font-size: 20px;
    font-weight: normal;
  }
  h5 {
    font-size: 16px;
    font-weight: normal;
  }
  .legenda {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.233333px;
  }
  .hint-text {
    font-size: 14px;
    line-height: 21px;
    &.external {
      max-width: 700px;
    }
    b {
      color: $marca-1;
      font-weight: 700;
    }
  }
  .message-box {
    border: 1px solid $cinza-l90;
    border-radius: 5px;
  }
  .error-text {
    color: $vermelho-0 !important;

    font-size: 12px !important;
    padding-left: 0 !important;

    margin: 0;
  }
  .v-dialog__container {
    display: inline-block !important;
  }
  .outer-frame-push {
    border: 2px solid $cinza-l70;
    border-radius: 40px 40px 0 0;
    border-bottom: 0;
    width: 430px;
    @media screen and (max-width: $tablet-size) {
      margin: 20px 30px 0;
      padding: 20px 10px 0;
    }
    @media screen and (min-width: $tablet-size) {
      margin: 20px 30px 0;
      padding: 20px 10px 0;
    }
    .camera-slot {
      border: 2px solid $cinza-l70;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 5vh;
      height: 20px;
      width: 20px;
      margin-right: 0.8vw;
    }
    .ear-speaker-slot {
      border: 2px solid $cinza-l70;
      border-radius: 30px;
      display: inline-block;
      margin-bottom: 50px;
      height: 20px;
      width: 100px;
      margin-left: 8px;
    }
    .inner-frame-mobile {
      border: 2px solid $cinza-l70;
      border-bottom: 0;
      height: 100%;
      overflow: hidden;
      .navbar {
        display: flex;
        flex-direction: row-reverse;
        background-color: $cinza-l90;
        padding: 15px;
        .navbar-padding {
          display: inline-block;
        }
      }
      .box-offers-placeholder {
        border-radius: 0 0 10px 10px;
        border: 1.5px solid $cinza-l70;
        height: 10px;
        margin: 0 15px 0 15px;
        border-top: 0;
      }
      .box-banner {
        height: 140px;
        margin: 20px;
        justify-content: center;
        align-items: center;
        display: flex;
      }
      .empty-banner {
        border: 1.5px solid $cinza-l70;
      }
      .box-buttons-group {
        flex-direction: row;
        display: flex;
        .box-button-placeholder {
          border-radius: 10px 10px 0 0;
          border: 1.5px solid $cinza-l70;
          border-bottom: 0;
          height: 300px;
          width: 300px;
          margin: 0 20px;
        }
        .first-button {
          margin-right: 0;
        }
      }
    }
  }
}
</style>
